'use client';

import { Button as CoreButton, cn, VariantProps } from '@scatterlab/zeta-core';
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  ComponentType,
  createElement,
  ElementType,
  memo,
  RefAttributes,
  SVGProps,
} from 'react';

import { button } from '../styles';

export type ButtonProps<Component extends ElementType = typeof CoreButton> =
  Omit<VariantProps<typeof button>, 'hasRightIcon' | 'hasLeftIcon'> &
    ComponentPropsWithoutRef<Component> &
    RefAttributes<ComponentRef<Component>> & {
      component?: Component;
      LeftIcon?: ComponentType<SVGProps<SVGSVGElement>>;
      RightIcon?: ComponentType<SVGProps<SVGSVGElement>>;
    };

const Button = <Component extends ElementType = typeof CoreButton>({
  component,
  className,
  size,
  variant,
  children,
  LeftIcon,
  RightIcon,
  ...props
}: ButtonProps<Component>) =>
  createElement(
    component ?? CoreButton,
    {
      className: cn(
        button({
          variant,
          size,
          hasRightIcon: !!RightIcon,
          hasLeftIcon: !!LeftIcon,
        }),
        className,
      ),
      ...props,
    },
    <>
      {LeftIcon && (
        <LeftIcon className="size-4 text-white group-disabled:text-gray-600" />
      )}
      {children}
      {RightIcon && (
        <RightIcon
          className={cn(
            'text-white group-disabled:text-gray-600',
            `${size === 'large' ? 'size-4' : 'size-[14px]'}`,
          )}
        />
      )}
    </>,
  );

export default memo(Button) as typeof Button;
